/**
 * TenantKillSwitchControl — tenant self-service per-capability halt UI.
 *
 * WQ-232 (Phase 4.9 α6). Appended to MyCapabilitiesPage below AllocationsTab.
 *
 * Renders one halt/restore toggle per CapabilityType plus a "Halt All
 * Capabilities" master toggle. Halt and restore actions open a
 * confirmation modal with an optional reason field. Polls every 30
 * seconds for state changes.
 *
 * Auto-enrollment: the toggle list comes directly from the backend
 * (KillSwitchService.list_halt_toggles iterates CapabilityType.__members__),
 * so a NEW capability type automatically appears as a new toggle row
 * with no per-capability frontend code change.
 *
 * Mutating actions require tenant_admin role on the JWT — the backend
 * returns 403 with TENANT_ADMIN_REQUIRED otherwise; this component
 * surfaces the error envelope as a banner. Read-only users (regular
 * tenant users) see toggle state but cannot flip toggles.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import * as svc from '../../services/tenantKillSwitchService';
import type { HaltToggle } from '../../services/tenantKillSwitchService';

const POLL_MS = 30_000;

const ADMIN_ROLES = new Set(['tenant_admin', 'admin', 'superadmin']);

type ConfirmAction =
  | { kind: 'halt'; capability: string }
  | { kind: 'restore'; capability: string }
  | { kind: 'halt-all' }
  | { kind: 'restore-all' }
  | null;

const TenantKillSwitchControl: React.FC = () => {
  const { authFetch, user, tenantId } = useAuth();
  const role = ((user as { role?: string } | null)?.role || '').toLowerCase();
  const isAdmin = ADMIN_ROLES.has(role);

  const [toggles, setToggles] = useState<HaltToggle[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmAction>(null);
  const [reason, setReason] = useState('');
  const pollHandle = useRef<ReturnType<typeof setInterval> | null>(null);

  const refresh = useCallback(async () => {
    try {
      setError(null);
      const data = await svc.listToggles(authFetch);
      setToggles(data);
    } catch (e) {
      setError((e as Error).message || 'Failed to load halt status');
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    if (!tenantId) return;
    refresh();
    pollHandle.current = setInterval(refresh, POLL_MS);
    return () => {
      if (pollHandle.current) clearInterval(pollHandle.current);
    };
  }, [tenantId, refresh]);

  const haltedCount = useMemo(
    () => (toggles ? toggles.filter((t) => t.is_halted).length : 0),
    [toggles],
  );
  const allHalted = useMemo(
    () => (toggles && toggles.length > 0 ? toggles.every((t) => t.is_halted) : false),
    [toggles],
  );

  const openConfirm = (action: ConfirmAction) => {
    setReason('');
    setConfirm(action);
  };

  const closeConfirm = () => {
    setConfirm(null);
    setReason('');
  };

  const performConfirm = async () => {
    if (!confirm) return;
    setPending(true);
    try {
      if (confirm.kind === 'halt') {
        await svc.halt(authFetch, confirm.capability, reason || undefined);
      } else if (confirm.kind === 'restore') {
        await svc.restore(authFetch, confirm.capability, reason || undefined);
      } else if (confirm.kind === 'halt-all') {
        await svc.haltAll(authFetch, reason || undefined);
      } else if (confirm.kind === 'restore-all') {
        await svc.restoreAll(authFetch, reason || undefined);
      }
      closeConfirm();
      await refresh();
    } catch (e) {
      setError((e as Error).message || 'Action failed');
    } finally {
      setPending(false);
    }
  };

  if (!tenantId) return null;

  return (
    <section
      data-testid="tenant-kill-switch-control"
      className="card"
      style={{
        padding: 20, marginTop: 24, borderRadius: 8,
        border: '1px solid var(--border)', background: 'var(--bg-card)',
      }}
    >
      <header style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18 }}>AI Capability Halt Controls</h2>
          <p style={{ margin: '4px 0 0', color: 'var(--text-secondary)', fontSize: 13 }}>
            Halt or restore individual AI capabilities for your tenant. Halts
            take effect immediately on new requests; in-flight calls complete
            on their natural timeline.
          </p>
        </div>
        {isAdmin && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              data-testid="halt-all-btn"
              type="button"
              disabled={pending || !toggles || allHalted}
              onClick={() => openConfirm({ kind: 'halt-all' })}
              style={{
                padding: '6px 14px', borderRadius: 6, fontSize: 13,
                background: 'var(--danger, #c0392b)', color: '#fff',
                border: 'none', cursor: pending ? 'not-allowed' : 'pointer',
                opacity: !toggles || allHalted ? 0.5 : 1,
              }}
            >
              Halt All
            </button>
            <button
              data-testid="restore-all-btn"
              type="button"
              disabled={pending || !toggles || haltedCount === 0}
              onClick={() => openConfirm({ kind: 'restore-all' })}
              style={{
                padding: '6px 14px', borderRadius: 6, fontSize: 13,
                background: 'var(--success, #27ae60)', color: '#fff',
                border: 'none', cursor: pending ? 'not-allowed' : 'pointer',
                opacity: !toggles || haltedCount === 0 ? 0.5 : 1,
              }}
            >
              Restore All
            </button>
          </div>
        )}
      </header>

      {error && (
        <div
          role="alert"
          data-testid="halt-error"
          style={{
            padding: 10, borderRadius: 6, marginBottom: 12,
            background: 'rgba(192, 57, 43, 0.1)', color: 'var(--danger, #c0392b)',
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {loading && <p data-testid="halt-loading">Loading capability halt status…</p>}

      {!loading && toggles && (
        <ul
          data-testid="halt-toggle-list"
          style={{ listStyle: 'none', padding: 0, margin: 0 }}
        >
          {toggles.map((t) => (
            <li
              key={t.capability_type}
              data-testid={`halt-toggle-${t.capability_type}`}
              data-halted={t.is_halted ? 'true' : 'false'}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 12px', marginBottom: 6, borderRadius: 6,
                borderLeft: `3px solid ${t.is_halted ? 'var(--danger, #c0392b)' : 'var(--success, #27ae60)'}`,
                background: t.is_halted ? 'rgba(192, 57, 43, 0.05)' : 'rgba(39, 174, 96, 0.05)',
              }}
            >
              <div>
                <strong style={{ textTransform: 'capitalize' }}>
                  {t.capability_type.replace(/_/g, ' ')}
                </strong>
                {t.is_halted && t.halt_record && (
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                    HALTED at {new Date(t.halt_record.halted_at).toLocaleString()}
                    {' by '}{t.halt_record.halted_by_role}
                    {t.halt_record.halt_reason ? ` — ${t.halt_record.halt_reason}` : ''}
                  </div>
                )}
                {!t.is_halted && (
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Active</div>
                )}
              </div>
              {isAdmin && (
                t.is_halted ? (
                  <button
                    type="button"
                    data-testid={`restore-btn-${t.capability_type}`}
                    disabled={pending}
                    onClick={() => openConfirm({ kind: 'restore', capability: t.capability_type })}
                    style={{
                      padding: '4px 10px', fontSize: 12, borderRadius: 4,
                      border: '1px solid var(--success, #27ae60)',
                      background: 'transparent', color: 'var(--success, #27ae60)',
                      cursor: pending ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Restore
                  </button>
                ) : (
                  <button
                    type="button"
                    data-testid={`halt-btn-${t.capability_type}`}
                    disabled={pending}
                    onClick={() => openConfirm({ kind: 'halt', capability: t.capability_type })}
                    style={{
                      padding: '4px 10px', fontSize: 12, borderRadius: 4,
                      border: '1px solid var(--danger, #c0392b)',
                      background: 'transparent', color: 'var(--danger, #c0392b)',
                      cursor: pending ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Halt
                  </button>
                )
              )}
            </li>
          ))}
        </ul>
      )}

      {confirm && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="halt-confirm-modal"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
          onClick={closeConfirm}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-card)', padding: 24, borderRadius: 8,
              maxWidth: 480, width: '90%',
            }}
          >
            <h3 style={{ marginTop: 0 }}>
              {confirm.kind === 'halt' && `Halt ${confirm.capability}?`}
              {confirm.kind === 'restore' && `Restore ${confirm.capability}?`}
              {confirm.kind === 'halt-all' && 'Halt all capabilities?'}
              {confirm.kind === 'restore-all' && 'Restore all capabilities?'}
            </h3>
            <label style={{ display: 'block', marginBottom: 12 }}>
              <span style={{ fontSize: 13 }}>Reason (optional)</span>
              <textarea
                data-testid="halt-reason-input"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                maxLength={500}
                style={{ width: '100%', marginTop: 4, padding: 6, borderRadius: 4 }}
              />
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                data-testid="halt-cancel-btn"
                onClick={closeConfirm}
                disabled={pending}
                style={{ padding: '6px 14px', borderRadius: 4 }}
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="halt-confirm-btn"
                onClick={performConfirm}
                disabled={pending}
                style={{
                  padding: '6px 14px', borderRadius: 4, color: '#fff',
                  background: confirm.kind.startsWith('halt')
                    ? 'var(--danger, #c0392b)' : 'var(--success, #27ae60)',
                  border: 'none',
                }}
              >
                {pending ? 'Working…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default TenantKillSwitchControl;
